import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {UnstyledButton} from '../Buttons/UnstyledButton';
import {Icon} from '../Icon/Icon';
import {GenericIconName} from '../Icon/IconName';

const PADDING_X = '1rem';
const PADDING_Y = '1.5rem';

interface Props {
  count?: number | string;
  selectedValues?: string;
  disableHorizontalPadding?: boolean;
  onClick?: () => void;
  onReset?: () => void;
  resetLabel?: string;
  title?: string | ReactNode;
  enableFilterFlyout?: boolean;
  className?: string;
}

// eslint-disable-next-line complexity
export const FilterCard: FC<React.PropsWithChildren<Props>> = ({
  children,
  count,
  disableHorizontalPadding,
  onClick,
  onReset,
  resetLabel,
  title,
  selectedValues,
  enableFilterFlyout = true,
  className,
}) => {
  const wrapperRole = onClick && !onReset && !children ? 'button' : 'none';
  const headerRole = onClick && !onReset && children ? 'button' : 'none';
  return (
    <Wrapper
      cursor={wrapperRole === 'button' ? 'pointer' : 'default'}
      data-testid="filterFlyoutSection"
      disableHorizontalPadding={disableHorizontalPadding}
      onClick={wrapperRole === 'button' ? onClick : undefined}
      role={wrapperRole}
      tabIndex={wrapperRole === 'button' ? 0 : undefined}
      $enableFilterFlyout={enableFilterFlyout}
      className={className}
    >
      <Header
        cursor={headerRole === 'button' ? 'pointer' : 'default'}
        data-testid="filterFlyoutSectionHeader"
        disableHorizontalPadding={disableHorizontalPadding}
        onClick={headerRole === 'button' ? onClick : undefined}
        role={headerRole}
        tabIndex={headerRole === 'button' ? 0 : undefined}
        $enableFilterFlyout={enableFilterFlyout}
      >
        <TitleWrapper>
          {title && (
            <Title
              data-testid="filterFlyoutSectionTitle"
              $enableFilterFlyout={enableFilterFlyout}
            >
              {title}
            </Title>
          )}
          {!enableFilterFlyout && count ? (
            <Count data-testid="filterFlyoutSectionCount">{count}</Count>
          ) : null}
        </TitleWrapper>
        {onClick && !resetLabel && (
          <NextIconWrapper>
            {count && enableFilterFlyout ? (
              <Count data-testid="filterFlyoutSectionCount">{count}</Count>
            ) : null}
            {selectedValues && enableFilterFlyout ? (
              <SelectedValue>{selectedValues}</SelectedValue>
            ) : null}

            <Icon
              asset={GenericIconName.ArrowRight}
              data-testid="filterFlyoutSectionNextIcon"
              height="18"
              width="18"
              fill={enableFilterFlyout ? COLORS.Grey1 : COLORS.Black}
            />
          </NextIconWrapper>
        )}
        {resetLabel && (
          <ResetButton
            data-testid="filterFlyoutSectionResetButton"
            onClick={onReset}
          >
            {resetLabel}
          </ResetButton>
        )}
      </Header>
      {children}
    </Wrapper>
  );
};

const Wrapper = styled.section<{
  cursor: string;
  disableHorizontalPadding?: boolean;
  $enableFilterFlyout: boolean;
}>`
  background: ${COLORS.White};
  padding: ${PADDING_Y} ${PADDING_X};
  cursor: ${({cursor}) => cursor};
  padding: ${({disableHorizontalPadding}) =>
    disableHorizontalPadding ? `${PADDING_Y} 0` : `${PADDING_Y} ${PADDING_X}`};
  ${props =>
    props.$enableFilterFlyout
      ? `border-bottom: 1px solid ${COLORS.Grey4};`
      : ''}

  &:focus {
    outline: none;
  }
`;

const Header = styled.header<{
  cursor: string;
  disableHorizontalPadding?: boolean;
  $enableFilterFlyout: boolean;
}>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${props =>
    !props.$enableFilterFlyout
      ? `padding: ${props.disableHorizontalPadding ? `0 ${PADDING_X}` : `0`};`
      : ''}
  box-sizing: border-box;
  cursor: ${({cursor}) => cursor};
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Title = styled.h4<{$enableFilterFlyout: boolean}>`
  font-weight: ${props =>
    props.$enableFilterFlyout ? FONT_WEIGHTS.mediumBold : FONT_WEIGHTS.bold};
  font-size: ${props =>
    props.$enableFilterFlyout ? FONT_SIZES.px14 : FONT_SIZES.px16};
  color: ${COLORS.Black};
  margin: 0 4px 0 0;
`;

const NextIconWrapper = styled.div`
  display: flex;
`;

const ResetButton = styled(UnstyledButton)`
  border: none;
  background: none;
  font-weight: ${FONT_WEIGHTS.mediumBold};
  font-size: ${FONT_SIZES.px14};
  color: ${COLORS.Grey1};
  cursor: pointer;

  &:focus {
    outline: none;
  }

  &:hover {
    color: ${COLORS.Black};
  }
`;

const Count = styled.span`
  align-items: center;
  background-color: ${COLORS.Black};
  border-radius: 10px;
  box-sizing: border-box;
  color: ${COLORS.White};
  display: inline-flex;
  flex-grow: 0;
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.bold};
  height: 17px;
  justify-content: center;
  padding: 0 6px;
  text-align: center;
  width: auto;
`;

const SelectedValue = styled.div`
  color: ${COLORS.Grey1};
  font-size: ${FONT_SIZES.px10};
  display: flex;
  align-items: center;
  text-transform: capitalize;
`;
